import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { IndianRupeeIcon, Download, CreditCard, Clock, CheckCircle, Loader2 } from "lucide-react";
import { Progress } from "../ui/progress";
import { toast } from "sonner";
import API from "../api/api01";

export function Fees() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const userFromStorage = JSON.parse(localStorage.getItem("user") || "{}");
  const studentId = userFromStorage?.id;

  useEffect(() => {
    if (studentId) {
      fetchFeeData();
    } else {
      setLoading(false);
    }
  }, [studentId]);

  const fetchFeeData = async () => {
    try {
      const response = await API.get(`/superadmin/student/fee-data/${studentId}`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching fee data:", error);
      toast.error("Failed to load fee details");
    } finally {
      setLoading(false);
    }
  };

  const feeStructure = data?.feeStructure || [];
  const paymentHistory = data?.paymentHistory || [];

  const totalAmount = feeStructure.reduce((sum: number, item: any) => sum + item.amount, 0);
  const paidAmount = feeStructure.reduce((sum: number, item: any) => sum + item.paid_amount, 0);
  const pendingAmount = totalAmount - paidAmount;
  const paidPercentage = totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;

  const handlePayment = () => {
    toast.success("Redirecting to payment gateway...");
  };

  const handleDownloadReceipt = () => {
    toast.success("Downloading receipt...");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1>Fee Management</h1>
        <p className="text-muted-foreground text-sm mt-1">View and manage your fee payments</p>
      </div>

      {feeStructure.length === 0 && paymentHistory.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No fee information found for your account.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Fee Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Total Fee</p>
                  <p className="text-2xl font-semibold">₹{totalAmount.toLocaleString()}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Paid</p>
                  <p className="text-2xl font-semibold text-secondary">₹{paidAmount.toLocaleString()}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-2xl font-semibold text-chart-3">₹{pendingAmount.toLocaleString()}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Payment Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Payment Progress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>₹{paidAmount.toLocaleString()} paid</span>
                  <span className="text-muted-foreground">{paidPercentage.toFixed(0)}%</span>
                </div>
                <Progress value={paidPercentage} className="h-2" />
              </div>
              {pendingAmount > 0 && (
                <div className="flex items-center justify-between p-4 rounded-lg bg-chart-3/10 border border-chart-3/20">
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-chart-3" />
                    <div>
                      <p className="text-sm font-medium">Pending Payment</p>
                      <p className="text-xs text-muted-foreground">Please complete your payments</p>
                    </div>
                  </div>
                  <Button
                    onClick={handlePayment}
                    className="gap-2 bg-primary text-white hover:bg-primary/90"
                  >
                    <CreditCard className="h-4 w-4 text-white" />
                    Pay ₹{pendingAmount.toLocaleString()}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Fee Structure */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Fee Structure</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {feeStructure.map((item: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg border border-border">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{item.item}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        ₹{item.paid_amount.toLocaleString()} / ₹{item.amount.toLocaleString()}
                      </p>
                    </div>
                    <Badge className={
                      item.status === "paid"
                        ? "bg-secondary/10 text-secondary hover:bg-secondary/20"
                        : "bg-chart-3/10 text-chart-3 hover:bg-chart-3/20"
                    }>
                      {item.status === "paid" ? (
                        <><CheckCircle className="h-3 w-3 mr-1" /> Paid</>
                      ) : (
                        <><Clock className="h-3 w-3 mr-1" /> Pending</>
                      )}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Payment History */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Payment History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {paymentHistory.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-4 italic">
                    No payment records found.
                  </div>
                ) : (
                  paymentHistory.map((payment: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <IndianRupeeIcon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{payment.description}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {payment.date} • {payment.method}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex items-center gap-3">
                        <div>
                          <p className="text-sm font-semibold">₹{payment.amount.toLocaleString()}</p>
                          <Badge className="bg-secondary/10 text-secondary hover:bg-secondary/20 text-xs mt-1">
                            {payment.status}
                          </Badge>
                        </div>
                        <Button variant="ghost" size="sm" onClick={handleDownloadReceipt}>
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
